import React from 'react';
import type { UsageBucket } from '../../../types/aiOps';
import { dayLabel, dayNumber, formatMoney } from '../format';

const pad = (value: number) => String(value).padStart(2, '0');

/** Every day of the month, so a gap reads as "nothing happened", not as a missing bar */
export function daysOfMonth(month: string, daily: UsageBucket[]): UsageBucket[] {
  const [year, index] = (month || '').split('-').map(Number);
  if (!year || !index) return daily;
  const byPeriod = new Map(daily.map((bucket) => [bucket.period, bucket]));
  const count = new Date(Date.UTC(year, index, 0)).getUTCDate();
  return Array.from({ length: count }, (_, offset) => {
    const period = `${year}-${pad(index)}-${pad(offset + 1)}`;
    return (
      byPeriod.get(period) ?? { period, calls: 0, inputTokens: 0, outputTokens: 0, costCents: 0, failures: 0 }
    );
  });
}

/**
 * Spend per day, as plain bars. Days that had a failed call carry a red tick
 * under them, so a day that cost money and returned nothing is visible.
 */
export const DailySpend: React.FC<{ month: string; daily: UsageBucket[]; className?: string }> = ({
  month,
  daily,
  className = '',
}) => {
  const days = daysOfMonth(month, daily);
  const peak = days.reduce((max, day) => Math.max(max, day.costCents), 0);
  const busiest = days.reduce((best, day) => (day.costCents > best.costCents ? day : best), days[0]);
  const failedDays = days.filter((day) => day.failures > 0).length;

  const summary = [
    `Daily spend for ${month}.`,
    peak > 0 && busiest ? `Highest on ${dayLabel(busiest.period)} at ${formatMoney(busiest.costCents)}.` : 'Nothing spent yet.',
    failedDays > 0 ? `${failedDays} ${failedDays === 1 ? 'day' : 'days'} had failed calls.` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <div role="img" aria-label={summary}>
        <div className="flex h-24 items-end gap-px sm:gap-[2px]">
          {days.map((day) => {
            const height = peak > 0 ? Math.max(day.costCents > 0 ? 3 : 0, (day.costCents / peak) * 100) : 0;
            return (
              <div
                key={day.period}
                className="flex h-full flex-1 flex-col justify-end"
                title={`${dayLabel(day.period)} · ${formatMoney(day.costCents)} · ${day.calls} ${
                  day.calls === 1 ? 'call' : 'calls'
                }${day.failures > 0 ? ` · ${day.failures} failed` : ''}`}
              >
                <span
                  className={`block w-full rounded-t-[3px] ${day.costCents > 0 ? 'bg-neutral-300' : 'bg-neutral-100'}`}
                  style={{ height: `${height}%`, minHeight: day.costCents > 0 ? 2 : 1 }}
                />
                <span className={`mt-px block h-[2px] w-full rounded-full ${day.failures > 0 ? 'bg-primary-600' : ''}`} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-1.5 flex justify-between text-[11px] tabular-nums text-neutral-400">
        <span>{dayNumber(days[0]?.period || '')}</span>
        <span>{dayNumber(days[days.length - 1]?.period || '')}</span>
      </div>

      {failedDays > 0 && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-neutral-400">
          <span aria-hidden="true" className="inline-block h-[2px] w-3 rounded-full bg-primary-600" />
          Days with failed calls
        </p>
      )}
    </div>
  );
};
